// BUILD_TIMESTAMP: 2026-05-23T13:05Z — cache-buster to force frontend rebuild
// (pipeline was serving an outdated bundle ignoring recent commits)
import React, { useEffect, Suspense, lazy } from "react";

// Build version string — referenced below to ensure webpack/craco can't
// strip this out as a "comment-only" change during minification. Bumping
// this string forces a new bundle hash on every deploy that touches it.
const __BUILD_VERSION__ = "2026-05-23T13:05Z-r1";
if (typeof window !== "undefined") {
  window.__RVINCENTE_BUILD__ = __BUILD_VERSION__;
}
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useAnalytics } from "./hooks/useAnalytics";
import { onForegroundMessage } from "./services/pushService";
import { Toaster } from "sonner";
import { Capacitor } from "@capacitor/core";

// Components (always loaded)
import CookieBanner from "./components/CookieBanner";
import AppInstallPrompt from "./components/AppInstallPrompt";
import ForceUpdateGuard from "./components/ForceUpdateGuard";
import LoadingScreen from "./components/LoadingScreen";
import OfflineNotice from "./components/OfflineNotice";
import { GlobalErrorBoundary, useGlobalErrorHandler } from "./components/GlobalErrorHandler";
import LatestVictoryPopup from "./components/LatestVictoryPopup";
import BirthdayPopup from "./components/BirthdayPopup";
import WinbackVipBanner from "./components/WinbackVipBanner";
import SchedinaAnnouncementGate from "./components/SchedinaAnnouncementGate";
import MySelectionAnnouncementGate from "./components/mySelection/MySelectionAnnouncementGate";
import ActivityPing from "./components/ActivityPing";
import AppDownloadPrompts, { StickyDownloadBanner } from "./components/AppDownloadPrompts";
import PriorityPopups, { AfterPriorityPopups } from "./components/consent/PriorityPopups";
import InviteDeepLink from "./components/InviteDeepLink";
import { captureInboundFbclid, captureInboundTtclid } from "./lib/metaPixel";
import { captureUtmParams } from "./lib/utmCapture";
import { captureInstallReferrer } from "./lib/installReferrer";
import { captureRefParam } from "./lib/inviteCode";
import { listenAppLinks } from "./lib/appLinks";

// iter250 — Capture inbound fbclid from the landing URL AS EARLY AS
// POSSIBLE (before React mounts, before any `fbq('track', ...)` call).
// Running this at module-import time means Meta ads attribution survives
// SPA navigations and page reloads for 90 days without depending on
// Pixel JS boot order.
captureInboundFbclid();
// iter269 — Android app: recover the landing fbclid from the Play Install Referrer.
captureInstallReferrer();
captureRefParam();
// iter278 — handle verified App Links (Google OAuth return + deep links).
listenAppLinks();
// iter254 — Same as above but for TikTok Ads (`ttclid` param → 7-day cookie).
captureInboundTtclid();
// iter251 — Capture internal UTM parameters (utm_source/campaign/content)
// so the admin "Acquisizione per campagna" widget can attribute signups
// to specific creatives. First-touch, sessionStorage-scoped.
captureUtmParams();

// Pages - LAZY LOADED (code splitting)
// Light pages loaded eagerly
import LoginPage from "./pages/LoginPage";
import PitchPreviewPage from "./pages/PitchPreviewPage";
import Dashboard from "./pages/Dashboard";
import AuthCallback from "./pages/AuthCallback";
import InvitePage from "./pages/InvitePage";
import InfluencerPage from "./pages/InfluencerPage";
import SharedSelectionPage from "./pages/SharedSelectionPage";
import DiarioPage from "./pages/DiarioPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ConfirmEmailPage from "./pages/ConfirmEmailPage";
import WinbackRedeemPage from "./pages/WinbackRedeemPage";
import LandingPage from "./pages/LandingPage";
import NearMissLandingPage from "./pages/NearMissLandingPage";

// Retry wrapper for lazy imports (handles stale chunk errors after deploys)
function lazyRetry(importFn) {
  return lazy(() => importFn().catch(async (error) => {
    console.warn('[RVINCENTE] Chunk load failed, retrying in 1.5s...', error?.message);
    await new Promise(resolve => setTimeout(resolve, 1500));
    try {
      return await importFn();
    } catch (retryError) {
      console.error('[RVINCENTE] Chunk retry failed, clearing cache and reloading...', retryError?.message);
      try {
        if ('caches' in window) {
          const names = await caches.keys();
          await Promise.all(names.map(n => caches.delete(n)));
        }
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister()));
        }
      } catch (e) { /* ignore cleanup errors */ }
      window.location.reload();
      return new Promise(() => {}); // Never resolves — page is reloading
    }
  }));
}

// Heavy pages loaded lazily with retry (only when user navigates to them)
const ChatPage = lazyRetry(() => import("./pages/ChatPage"));
const AdminPage = lazyRetry(() => import("./pages/AdminPage"));
const VIPPage = lazyRetry(() => import("./pages/VIPPage"));
const ProfilePage = lazyRetry(() => import("./pages/ProfilePage"));
const BonusPage = lazyRetry(() => import("./pages/BonusPage"));
const MultiregistrazionePage = lazyRetry(() => import("./pages/MultiregistrazionePage"));
const DemoPage = lazyRetry(() => import("./pages/DemoPage"));
const MatchDetailPage = lazyRetry(() => import("./pages/MatchDetail").then(m => ({ default: m.MatchDetailWrapper })));
const IAPage = lazyRetry(() => import("./pages/IAPage"));
const GoldPage = lazyRetry(() => import("./pages/GoldPage"));
const Admin2FAPage = lazyRetry(() => import("./pages/Admin2FAPage"));

// Legal pages (rarely visited)
const LazyPrivacyPolicy = lazyRetry(() => import("./pages/LegalPages").then(m => ({ default: m.PrivacyPolicyPage })));
const LazyCookiePolicy = lazyRetry(() => import("./pages/LegalPages").then(m => ({ default: m.CookiePolicyPage })));
const LazyTermsPage = lazyRetry(() => import("./pages/LegalPages").then(m => ({ default: m.TermsPage })));
const LazyResponsibleGaming = lazyRetry(() => import("./pages/LegalPages").then(m => ({ default: m.ResponsibleGamingPage })));
const LazyDisclaimer = lazyRetry(() => import("./pages/LegalPages").then(m => ({ default: m.DisclaimerPage })));

// Configure axios Bearer token interceptor
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.__retryCount = config.__retryCount || 0;
  return config;
});

// Auto-retry on 502/503/504 (server temporarily unavailable during deploys)
axios.interceptors.response.use(
  (response) => {
    window.dispatchEvent(new Event('serverUp'));
    return response;
  },
  async (error) => {
    const config = error.config;
    const status = error.response?.status;
    if (!config) return Promise.reject(error);
    config.__retryCount = config.__retryCount || 0;
    // Retry up to 3 times on server errors with increasing delay
    // Skip retry per endpoint non-idempotenti (invio email massivo) — un retry
    // farebbe partire una seconda campagna duplicando 850+ email.
    const isNonIdempotent = config.__skipRetry ||
      (typeof config.url === 'string' && (
        config.url.includes('/marketing/send') ||
        config.url.includes('/marketing/send-test') ||
        config.url.includes('/broadcast')
      ));
    if (!isNonIdempotent && [502, 503, 504, 520].includes(status) && config.__retryCount < 3) {
      config.__retryCount += 1;
      await new Promise(r => setTimeout(r, config.__retryCount * 2000));
      return axios(config);
    }
    // Only show serverDown banner on true server errors (not auth or client errors)
    if ([502, 503, 504, 520].includes(status)) {
      window.dispatchEvent(new Event('serverDown'));
      // Auto-recovery: check server again after 10s
      setTimeout(async () => {
        try {
          await axios.get('/api/health', { timeout: 5000, __skipRetry: true });
          window.dispatchEvent(new Event('serverUp'));
        } catch {}
      }, 10000);
    }
    return Promise.reject(error);
  }
);

// Suspense fallback for lazy loaded pages
const PageLoader = () => (
  <div style={{ minHeight: '100vh', background: '#1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: '24px', height: '24px', border: '3px solid #333', borderTopColor: '#F5A623', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [forceReady, setForceReady] = React.useState(false);
  
  React.useEffect(() => {
    if (loading && !forceReady) {
      const timer = setTimeout(() => setForceReady(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [loading, forceReady]);
  
  if (loading && !forceReady) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
};

const LazyRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

const AppRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // No announcement/victory popups on landings and in chat (keeps CTA / message box visible)
  const isLandingRoute = ['/win', '/quasi', '/chat'].includes(location.pathname);

  // Return to a page requested before login (e.g. /win chat button), valid 30 min
  useEffect(() => {
    if (!user) return;
    try {
      const tapId = localStorage.getItem('win_tap_pending');
      if (tapId) {
        localStorage.removeItem('win_tap_pending');
        axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/landing/win/cta-tap/${encodeURIComponent(tapId)}/claim`).catch(() => {});
      }
    } catch {}
    try {
      const raw = localStorage.getItem('post_login_redirect');
      if (!raw) return;
      localStorage.removeItem('post_login_redirect');
      const { path, ts } = JSON.parse(raw);
      if (typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && Date.now() - ts < 30 * 60 * 1000) {
        navigate(path, { replace: true });
      }
    } catch {}
  }, [user?.user_id]);

  // Analytics: fire app_open (once/session) + page_view on every route
  // change for authenticated users only. Guests are never tracked.
  useAnalytics(user);

  // iter259 (2026-10-05) — Blocca indicizzazione bot social su tutte le
  // route tranne la home `/`. Il tag <meta name="robots"> è dinamicamente
  // aggiornato a ogni cambio pagina. Bot "well-behaved" (Googlebot, Bingbot,
  // FacebookExternalHit, Bytespider) lo rispettano. L'intento è impedire
  // a Meta/TikTok di analizzare l'app durante le campagne (banner Meta
  // ancora attivo → qualunque pagina indicizzata = rischio ban).
  useEffect(() => {
    const path = location.pathname || "/";
    const isHome = path === "/" || path === "/home";
    let tag = document.querySelector('meta[name="robots"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      document.head.appendChild(tag);
    }
    tag.setAttribute(
      "content",
      isHome
        ? "index,follow"
        : "noindex,nofollow,noarchive,nosnippet,noimageindex"
    );
  }, [location.pathname]);

  // Expose queryClient globally for app resume handler
  useEffect(() => {
    window.queryClient = queryClient;
    return () => { delete window.queryClient; };
  }, []);

  // Auto-update: check if a new deploy is available and reload silently
  useEffect(() => {
    let currentHash = null;
    const checkForUpdate = async () => {
      try {
        const res = await fetch('/index.html?_t=' + Date.now(), { cache: 'no-store' });
        const html = await res.text();
        const match = html.match(/main\.[a-f0-9]+\.js/);
        const hash = match ? match[0] : null;
        if (currentHash && hash && currentHash !== hash) {
          // New version detected — clear caches and reload
          if ('caches' in window) {
            const names = await caches.keys();
            await Promise.all(names.map(n => caches.delete(n)));
          }
          window.location.reload();
          return;
        }
        currentHash = hash;
      } catch {}
    };
    checkForUpdate();
    const interval = setInterval(checkForUpdate, 5 * 60 * 1000);
    const handleResume = () => {
      // BUG 2 fix: marker globale che indica il momento del resume.
      // useConversations e altri hook lo usano per evitare di mostrare
      // toast errori durante il warmup LTE (primi 5s).
      try { window.__rvincente_resume_at = Date.now(); } catch {}
      setTimeout(checkForUpdate, 2000);
      // Invalida solo vip-predictions al resume: React Query + IndexedDB
      // gia' gestiscono il resto con refetchOnWindowFocus.
      try {
        if (window.queryClient) {
          window.queryClient.invalidateQueries({ queryKey: ['vip-predictions'] });
        }
      } catch {}
    };
    const handleFocus = () => {
      try { window.__rvincente_resume_at = Date.now(); } catch {}
    };
    window.addEventListener('appResume', handleResume);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('appResume', handleResume);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, []);

  // Handle pending navigation from native push notification click
  useEffect(() => {
    const handlePendingNav = () => {
      const pending = localStorage.getItem('pending_notification_nav');
      if (pending) {
        localStorage.removeItem('pending_notification_nav');
        // External URLs → open in browser, internal paths → navigate in app
        if (pending.startsWith('http://') || pending.startsWith('https://')) {
          window.open(pending, '_blank');
        } else {
          // Push notifications point to pages that MUST show fresh data
          // (new VIP prediction, new victory, new chat message).
          // Invalida subito la cache React Query + IndexedDB per evitare
          // di mostrare dati vecchi all'utente.
          try {
            if (window.queryClient) {
              // Forza refetch delle query piu' critiche
              window.queryClient.invalidateQueries({ queryKey: ['vip-predictions'] });
              window.queryClient.invalidateQueries({ queryKey: ['victory-gallery'] });
              window.queryClient.invalidateQueries({ queryKey: ['conversations'] });
              window.queryClient.invalidateQueries({ queryKey: ['matches'] });
            }
            // Rimuovi anche la cache IndexedDB delle VIP predictions
            import('./lib/appCache').then(m => {
              try { m.removeCache && m.removeCache('vip-predictions'); } catch {}
              try { m.removeCache && m.removeCache('victory-gallery'); } catch {}
            }).catch(() => {});
          } catch {}
          navigate(pending, { replace: true });
        }
      }
    };

    // Check on mount (app cold start from notification)
    handlePendingNav();
    // Retry after short delay — covers race with index.js async listener setup
    const retryTimer = setTimeout(handlePendingNav, 500);

    // Listen for immediate navigation events (app already in foreground)
    window.addEventListener('notificationNav', handlePendingNav);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') handlePendingNav();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimeout(retryTimer);
      window.removeEventListener('notificationNav', handlePendingNav);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [navigate]);

  // Handle app resume from background (fix white screen on Android/iOS)
  useEffect(() => {
    let lastPause = 0;

    // Method 1: Capacitor native listener (most reliable on native)
    if (Capacitor.isNativePlatform()) {
      (async () => {
        const { App: CapApp } = await import('@capacitor/app');
        CapApp.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            lastPause = Date.now();
          } else {
            handleResume(Date.now() - lastPause);
          }
        });
      })();
    }

    // Method 2: visibilitychange (works on all platforms as backup)
    const onVisChange = () => {
      if (document.visibilityState === 'hidden') {
        lastPause = Date.now();
      } else if (document.visibilityState === 'visible' && lastPause > 0) {
        handleResume(Date.now() - lastPause);
      }
    };
    document.addEventListener('visibilitychange', onVisChange);

    function handleResume(pausedMs) {
      // FIRST: paint the dark background immediately on resume. iOS/Android
      // WebView may briefly show a stale frame or white flash before React
      // reconciliation runs. We force a style touch on <body> to trigger a
      // repaint with the correct dark background defined in index.html.
      try {
        document.body.style.backgroundColor = document.documentElement.classList.contains('theme-light') ? '#F4ECDD' : '#1A1A1A';
        // Force reflow — cheap trick that guarantees the browser re-paints
        // before returning to the event loop.
        // eslint-disable-next-line no-unused-expressions
        document.body.offsetHeight;
      } catch { /* no-op */ }

      // Show the boot splash to mask any native WebView white flash during
      // the resume handshake. We hide it after 2 rAFs once we verify the
      // root actually has content (or after a short timeout as safety net).
      if (pausedMs > 500 && typeof window.__showBootSplash === 'function') {
        try {
          window.__showBootSplash();
          const hideSplashWhenReady = (attempt) => {
            const root = document.getElementById('root');
            const hasContent = root && root.children.length > 0 && root.offsetHeight > 0;
            if (hasContent || attempt >= 8) {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  try { window.__hideBootSplash && window.__hideBootSplash(); } catch (_) { /* no-op */ }
                });
              });
            } else {
              setTimeout(() => hideSplashWhenReady(attempt + 1), 120);
            }
          };
          hideSplashWhenReady(0);
        } catch { /* no-op */ }
      }

      // Invalidate cache on resume
      if (window.queryClient && pausedMs > 5000) {
        window.queryClient.invalidateQueries();
      }
      
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('appResume', { 
        detail: { pausedMs, timeSinceLastActive: pausedMs } 
      }));

      // Fix white screen: check multiple times with increasing delays
      if (pausedMs > 3000) {
        const checkAndReload = (attempt) => {
          const root = document.getElementById('root');
          // Check if page is blank: no height, no visible content, or body has no children rendered
          const isBlank = !root || 
            root.offsetHeight === 0 || 
            root.children.length === 0 ||
            (root.innerHTML.length < 100);
          if (isBlank) {
            if (attempt < 3) {
              // Give React more time to re-render before reloading
              setTimeout(() => checkAndReload(attempt + 1), 500);
            } else {
              window.location.reload();
            }
          }
        };
        // Check at 100ms, 600ms, 1100ms after resume
        setTimeout(() => checkAndReload(0), 100);
      }
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisChange);
    };
  }, []);

  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (location.pathname === "/auth/google/callback") return <AuthCallback />;
  return (
    <>
      <WinbackVipBanner />
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/pitch-preview" element={<PitchPreviewPage />} />
      <Route path="/r/:token" element={<WinbackRedeemPage />} />
      <Route path="/invito/:code" element={<InvitePage />} />
      <Route path="/inf/:code" element={<InfluencerPage />} />
      <Route path="/s/:code" element={<SharedSelectionPage />} />
      <Route path="/book" element={<LandingPage pageKey="book" />} />
      <Route path="/maggiorate" element={<LandingPage pageKey="maggiorate" />} />
      <Route path="/quasi" element={<NearMissLandingPage />} />
      <Route path="/win" element={<NearMissLandingPage key="win" endpoint="win" slug="win" fileName="multipla-vincente-rvincente" alt="Multipla vincente" />} />
      <Route path="/demo" element={<LazyRoute><DemoPage /></LazyRoute>} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/conferma-email" element={<ProtectedRoute><ConfirmEmailPage /></ProtectedRoute>} />
      <Route path="/privacy-policy" element={<LazyRoute><LazyPrivacyPolicy /></LazyRoute>} />
      <Route path="/cookie-policy" element={<LazyRoute><LazyCookiePolicy /></LazyRoute>} />
      <Route path="/terms" element={<LazyRoute><LazyTermsPage /></LazyRoute>} />
      <Route path="/gioco-responsabile" element={<LazyRoute><LazyResponsibleGaming /></LazyRoute>} />
      <Route path="/disclaimer" element={<LazyRoute><LazyDisclaimer /></LazyRoute>} />
      {process.env.REACT_APP_HIDE_BOOKMAKERS !== 'true' && <Route path="/multiregistrazione" element={<ProtectedRoute><MultiregistrazionePage /></ProtectedRoute>} />}
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/match/:id" element={<LazyRoute><MatchDetailPage /></LazyRoute>} />
      {process.env.REACT_APP_HIDE_BOOKMAKERS !== 'true' && <Route path="/bonus" element={<ProtectedRoute><BonusPage /></ProtectedRoute>} />}
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/ia" element={<LazyRoute><IAPage /></LazyRoute>} />
      <Route path="/diario" element={<ProtectedRoute><DiarioPage /></ProtectedRoute>} />
      <Route path="/vip" element={<LazyRoute><VIPPage /></LazyRoute>} />
      <Route path="/gold" element={<ProtectedRoute><GoldPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/admin/2fa" element={<ProtectedRoute><Admin2FAPage /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    <PriorityPopups />
    <AfterPriorityPopups>
      <InviteDeepLink />
      {!isLandingRoute && <LatestVictoryPopup />}
      <BirthdayPopup />
      {!isLandingRoute && <SchedinaAnnouncementGate />}
      {!isLandingRoute && !location.pathname.startsWith('/s/') && <MySelectionAnnouncementGate />}
      <ActivityPing />
    </AfterPriorityPopups>
    </>
  );
};

function App() {
  // Install global error handlers (window.onerror, unhandledrejection)
  useGlobalErrorHandler();

  console.log('[RVINCENTE] App component rendering...');

  // ONE-TIME IndexedDB → React Query seed at app bootstrap.
  // This replaces the per-hook useEffect seeding that was racing with
  // React Query's internal fetch scheduler and triggering a tight refetch
  // loop (~83 req/s on /api/chat/conversations). Running it here guarantees
  // the seed happens exactly once per app lifetime, BEFORE any consumer
  // component mounts, so there's no race.
  //
  // DEFENSIVE: only seed if the cached value is a plain array. A stale or
  // malformed cache (e.g. older `{_data, _cachedAt}` wrapper whose inner
  // `_data` is null, an object, or otherwise non-array) would cause the
  // consumer's `.filter()` / `.map()` calls to throw "X.filter is not a
  // function" from the GlobalErrorBoundary.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getCached } = await import('./lib/appCache');
        const keys = ['conversations', 'vip-predictions', 'victory-gallery', 'bonuses'];
        for (const k of keys) {
          if (cancelled) return;
          if (queryClient.getQueryData([k])) continue;
          const cached = await getCached(k);
          if (!cached) continue;
          const realData = (cached && typeof cached === 'object' && !Array.isArray(cached) && cached._data !== undefined)
            ? cached._data
            : cached;
          // Only seed if it's actually an array (all 4 queries return arrays)
          if (!Array.isArray(realData)) continue;
          queryClient.setQueryData([k], realData);
        }
      } catch { /* cache seed is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load admin league `is_top` overrides + `manual_order` map at bootstrap so
  // the Home accordion + sort order reflect admin choices on first render.
  //
  // 2026-02 (production bug — Bundesliga case): these maps used to be
  // loaded ONCE on App mount, but in the native Capacitor WebView the app
  // can stay alive for days. When admin pins a league mid-day, the user's
  // running app never re-fetched the pin map — Home kept using stale data
  // (Bundesliga pinned in admin panel showed at position 7 on Home).
  // Fix: refetch on app resume (visibility) + every 3 min while active.
  // Endpoints are tiny (single Mongo lookup, ~200 bytes each).
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    const refetchOverrides = async () => {
      try {
        const { setLeagueTopOverrides, setLeagueManualOrders, setLeaguePinOverrides } = await import('./lib/leagues');
        const [topRes, orderRes, pinRes] = await Promise.all([
          axios.get(`${API}/league-top-overrides`, { __skipRetry: true }).catch(() => null),
          axios.get(`${API}/league-manual-orders`, { __skipRetry: true }).catch(() => null),
          axios.get(`${API}/league-pins`, { __skipRetry: true }).catch(() => null),
        ]);
        if (topRes?.data?.overrides) setLeagueTopOverrides(topRes.data.overrides);
        if (orderRes?.data?.orders) setLeagueManualOrders(orderRes.data.orders);
        if (pinRes?.data?.pins) setLeaguePinOverrides(pinRes.data.pins);
        // After overrides refresh, invalidate the matches query so the Home
        // re-runs `buildLeagueGroups` with the fresh pin/top maps. Without
        // this, matches may already be cached (via useQuery) → group order
        // stays stale until the next natural refetch cycle.
        try {
          if (window.queryClient) {
            window.queryClient.invalidateQueries({ queryKey: ['matches'] });
          }
        } catch { /* non-blocking */ }
      } catch { /* best-effort: fall back to hardcoded rules */ }
    };
    // Initial load
    refetchOverrides();
    // Periodic refresh (3 min) while app is active
    const interval = setInterval(refetchOverrides, 3 * 60 * 1000);
    // Refresh on visibility change (browser tab focus / native app resume)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refetchOverrides();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('appResume', refetchOverrides);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('appResume', refetchOverrides);
    };
  }, []);

  useEffect(() => {
    let unsubscribe;
    try {
      unsubscribe = onForegroundMessage((payload) => {
        console.log('Foreground notification:', payload);
      });
    } catch {
      // Firebase messaging not available — safe to ignore
    }

    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        window.location.href = event.data.url;
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  return (
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <div className="App max-w-md mx-auto relative">
          <Toaster position="top-center" richColors theme="dark" />
          <OfflineNotice />
          <CookieBanner />
          <BrowserRouter>
            <AuthProvider>
              {/* StickyDownloadBanner must be INSIDE AuthProvider so it can
                  gate itself on `useAuth().user` — guests must NOT see any
                  app download push (would break Meta Pixel attribution). */}
              <StickyDownloadBanner />
              <AppRouter />
              <AfterPriorityPopups><AppInstallPrompt /></AfterPriorityPopups>
              <ForceUpdateGuard />
              {/* iter247 — 4 touchpoint download push */}
              <AfterPriorityPopups><AppDownloadPrompts /></AfterPriorityPopups>
            </AuthProvider>
          </BrowserRouter>
        </div>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
